import { Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInputWithCountry from 'react-phone-number-input'

import { PageContext } from '../../../contexts'
import { CrossIcon } from '../../../icons/icons'
import { formRequest } from '../../../utils/formRequest'
import styles from './MobileTabletCallbackForm.module.css'

type Props = {
    unitId?: string | number
    isSuccess: boolean
    setIsSuccess: Dispatch<SetStateAction<boolean>>
}

const MobileTabletCallbackForm: React.FC<Props> = ({ unitId = '', isSuccess, setIsSuccess }) => {
    const { t } = useTranslation()
    const { clickedUnit, setClickedUnit, activeButton, setActiveButton } = useContext(PageContext)

    const [formValues, setFormValues] = useState({
        firstName: { value: '', error: false },
        lastName: { value: '', error: false },
        email: { value: '', error: false },
        phone: { value: '', error: false },
    })

    // Fonction utilitaire pour valider les champs
    const validateField = (name: string, value: string) => {
        if (name === 'email') return /^[a-zA-Z0-9._%+-\u0600-\u06FF]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
        if (name === 'phone') return value.length > 4
        return value.trim().length > 0
    }

    const handleFormValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target

        setFormValues(prev => ({
            ...prev,
            [name]: {
                value,
                error: !validateField(name, value), // Valide le champ et met à jour l'erreur
            },
        }))
    }

    const handleSubmitForm = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
        e.preventDefault()

        const { firstName, lastName, email, phone } = formValues
        let canSend = true

        // Valider tous les champs
        Object.keys(formValues).forEach(field => {
            if (!validateField(field, formValues[field as keyof typeof formValues].value)) {
                setFormValues(prev => ({
                    ...prev,
                    [field]: { ...prev[field as keyof typeof formValues], error: true },
                }))
                canSend = false
            }
        })

        if (!canSend) return

        formRequest([
            { label: 'first_name', value: firstName.value },
            { label: 'last_name', value: lastName.value },
            { label: 'email', value: email.value },
            { label: 'unit_id', value: unitId },
            { label: 'phone', value: phone.value },
            { label: 'referrer', value: 'https://agre.speed.hebergement-com4design.fr/crm/register-your-interest/' },
            { label: 'referrer_title', value: 'Register your interest' },
            { label: 'form_id', value: '9a51e7b' },
            { label: 'queried_id', value: '769' },
            { label: 'post_id', value: '769' },
        ])
            .then(() => {
                setFormValues({
                    firstName: { value: '', error: false },
                    lastName: { value: '', error: false },
                    email: { value: '', error: false },
                    phone: { value: '', error: false },
                })
                setIsSuccess(true)
            })
            .catch(() => {
                setIsSuccess(false)
            })
    }

    const handleClose = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
        e.preventDefault()
        clickedUnit && setClickedUnit(null)
        setIsSuccess(false)
        activeButton === t(`nav.register`) && setActiveButton(null)
    }

    return (
        <form className={styles.container}>
            {!isSuccess ? (
                <>
                    <div className={styles.tabletRow}>
                        <div>
                            <input
                                type='text'
                                className={styles.input}
                                placeholder={t(`register.firstname`)}
                                onChange={handleFormValueChange}
                                value={formValues.firstName.value}
                                name='firstName'
                            />
                            <div
                                className={styles.error}
                                style={{
                                    opacity: formValues.firstName.error ? 1 : 0,
                                    maxHeight: formValues.firstName.error ? '50px' : '0',
                                }}>
                                {formValues.firstName.error && <div className={styles.error}>{t(`callback.invalidFirstName`)}</div>}
                            </div>
                        </div>
                        <div>
                            <input
                                type='text'
                                className={styles.input}
                                placeholder={t(`register.lastname`)}
                                value={formValues.lastName.value}
                                onChange={handleFormValueChange}
                                name='lastName'
                            />
                            <div
                                className={styles.error}
                                style={{
                                    opacity: formValues.lastName.error ? 1 : 0,
                                    maxHeight: formValues.lastName.error ? '50px' : '0',
                                }}>
                                {formValues.lastName.error && <div className={styles.error}>{t(`callback.invalidLastName`)}</div>}
                            </div>
                        </div>
                    </div>
                    <div className={styles.tabletRow}>
                        <div>
                            <input
                                type='email'
                                className={styles.input}
                                placeholder={t(`register.email`)}
                                value={formValues.email.value}
                                onChange={handleFormValueChange}
                                name='email'
                            />
                            <div
                                className={styles.error}
                                style={{
                                    opacity: formValues.email.error ? 1 : 0,
                                    maxHeight: formValues.email.error ? '50px' : '0',
                                }}>
                                {formValues.email.error && <div className={styles.error}>{t(`callback.invalidEmail`)}</div>}
                            </div>
                        </div>
                        <div className={styles.tabletPhoneInput}>
                            <PhoneInputWithCountry
                                placeholder='Enter phone number'
                                international
                                defaultCountry='AE'
                                value={formValues.phone.value}
                                onChange={e => setFormValues(prev => ({ ...prev, phone: { value: e ?? '', error: false } }))}
                                className={styles.PhoneInput}
                            />
                        </div>
                    </div>

                    <button className={styles.formButton} onClick={handleSubmitForm}>
                        {t(`register.send`)}
                    </button>
                </>
            ) : (
                <div className={styles.successMessage}>
                    <p className={styles.successTitle}>Thank you !</p>
                    <p className={styles.successText}>Your interest {unitId !== '' && 'in this home'} has been sent !</p>
                    <p className={styles.successSubText}>Someone will be in touch with you shortly.</p>
                </div>
            )}
        </form>
    )
}

export default MobileTabletCallbackForm
